import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout } from '../../components/Layout/Layout'
import { Modal } from '../../components/Modal/Modal'
import { GenerateReport } from './GenerateReport'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { fmtCurEUR } from '../../design/charts'
import { exportRowsAsCsv } from '../../utils/csv'
import { G6_REPORTS_DEMO } from '../../design/demoData'
import { useToast } from '../../design/useToast'
import { Toast } from '../../design/Toast'
import { T, useContent } from '../../content/ContentProvider'

// Convert backend StatementReport → row shape used by the design table
function mapApiToRow(r) {
  const balance = r.balance || {}
  const amount = balance.amount ? balance.amount / Math.pow(10, balance.scale ?? 2) : 0
  const inv = r.invoicingParty?.name || r.invoicingPartyRics || '—'
  const rec = r.invoiceRecipient?.name || r.invoiceRecipientRics || '—'
  return {
    id: r.reportId || r.id,
    direction: r.direction || 'outgoing',
    period: r.startStatementPeriod ? r.startStatementPeriod.slice(0, 7) : '—',
    partner: rec,
    rics: r.invoicingPartyRics || '—',
    amount,
    currency: balance.currency || 'EUR',
    status: (r.status || 'sent').toLowerCase(),
    sent: r.creationDate ? new Date(r.creationDate).toLocaleString('en-GB') : 'pending',
    records: r.accountingRecords?.length || 0,
    format: 'CSV+JSON',
  }
}

export function Reports() {
  const { t } = useContent()
  const navigate = useNavigate()
  const [apiRows, setApiRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dir, setDir] = useState('all')
  const [search, setSearch] = useState('')
  const [downloadingId, setDownloadingId] = useState(null)

  // Email template
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [templateOpen, setTemplateOpen] = useState(false)
  const [templateLoading, setTemplateLoading] = useState(false)
  const { toast, showToast } = useToast()

  const fetchReports = async () => {
    setLoading(true)
    setError('')
    try {
      const r = await api.get('/api/admin/reports?size=100')
      const data = Array.isArray(r.data) ? r.data : r.data.content || []
      setApiRows(data)
    } catch (err) {
      setError(err.response?.data?.message || t('reports.list.error.load'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReports()
    api
      .get('/api/admin/email-template')
      .then((r) => {
        setEmailSubject(r.data.subject || '')
        setEmailBody(r.data.body || '')
      })
      .catch(() => {})
  }, [])

  const usingDemo = apiRows.length === 0 && !loading
  const sourceRows = useMemo(
    () => (usingDemo ? G6_REPORTS_DEMO : apiRows.map(mapApiToRow)),
    [usingDemo, apiRows]
  )

  const rows = useMemo(() => {
    let r = sourceRows
    if (dir !== 'all') r = r.filter((row) => row.direction === dir)
    if (search.trim()) {
      const s = search.toLowerCase()
      r = r.filter(
        (row) =>
          row.id.toLowerCase().includes(s) ||
          (row.partner || '').toLowerCase().includes(s) ||
          (row.rics || '').toLowerCase().includes(s)
      )
    }
    return r
  }, [sourceRows, dir, search])

  const totalAmount = rows.reduce((s, r) => s + r.amount, 0)
  const counts = useMemo(
    () => ({
      all: sourceRows.length,
      outgoing: sourceRows.filter((r) => r.direction === 'outgoing').length,
      incoming: sourceRows.filter((r) => r.direction === 'incoming').length,
      overdue: sourceRows.filter((r) => r.status === 'overdue' || r.status === 'queued').length,
    }),
    [sourceRows]
  )

  const handleDownload = async (reportId, format) => {
    if (usingDemo) {
      alert(t('reports.list.demoDownloadAlert'))
      return
    }
    setDownloadingId(`${reportId}-${format}`)
    try {
      const response = await api.get(`/api/reporting/statementreports/${reportId}/export`, {
        params: { format },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `report-${reportId}-${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'json'}`
      )
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(t('reports.list.error.download'))
    } finally {
      setDownloadingId(null)
    }
  }

  const handleTemplateSave = async () => {
    setTemplateLoading(true)
    try {
      await api.put('/api/admin/email-template', { subject: emailSubject, body: emailBody })
      showToast('ok', t('reports.list.template.saved'))
    } catch (err) {
      showToast('err', err.response?.data?.message || t('reports.list.template.saveFailed'))
    } finally {
      setTemplateLoading(false)
    }
  }

  return (
    <Layout crumb={t('reports.list.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h"><T k="reports.list.title" /></h1>
              <div className="page-head__sub">
                <T k="reports.list.subtitle" />
                {usingDemo && t('reports.list.subtitleDemo')}
              </div>
            </div>
            <div className="page-head__actions">
              <button className="btn" onClick={() => setTemplateOpen((v) => !v)}>
                {Icon.settings({ s: 12 })} <T k="reports.list.action.emailTemplate" />
              </button>
              <button
                className="btn"
                onClick={() =>
                  exportRowsAsCsv(
                    rows,
                    [
                      { key: 'id', label: t('reports.list.csv.reportId') },
                      { key: 'direction', label: t('reports.list.csv.direction') },
                      { key: 'period', label: t('reports.list.csv.period') },
                      { key: 'partner', label: t('reports.list.csv.partner') },
                      { key: 'rics', label: t('reports.list.csv.invoicingRics') },
                      { key: 'amount', label: t('reports.list.csv.amount') },
                      { key: 'currency', label: t('reports.list.csv.currency') },
                      { key: 'status', label: t('reports.list.csv.status') },
                      { key: 'records', label: t('reports.list.csv.records') },
                      { key: 'sent', label: t('reports.list.csv.delivered') },
                    ],
                    'mas-g6-reports-index'
                  )
                }
                title={t('reports.list.action.exportTitle')}
              >
                {Icon.download({ s: 12 })} <T k="reports.list.action.export" />
              </button>
              <button className="btn btn--primary" onClick={() => setIsModalOpen(true)}>
                {Icon.upload({ s: 12 })} <T k="reports.list.action.generate" />
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <div className="grid-3">
          <div className="kpi kpi--accent">
            <div className="kpi__label"><T k="reports.list.kpi.totalCleared" /></div>
            <div className="kpi__value">{fmtCurEUR(totalAmount)}</div>
            <div className="kpi__sub">
              <span><T k="reports.list.kpi.acrossReports" vars={{ count: rows.length }} /></span>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi__label"><T k="reports.list.kpi.recordsDelivered" /></div>
            <div className="kpi__value">{rows.reduce((s, r) => s + (r.records || 0), 0).toLocaleString('en-US')}</div>
            <div className="kpi__sub">
              <span><T k="reports.list.kpi.acrossReports" vars={{ count: rows.length }} /></span>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi__label"><T k="reports.list.kpi.overdueQueued" /></div>
            <div className="kpi__value" style={{ color: 'var(--warn-dark)' }}>
              {counts.overdue}
            </div>
            <div className="kpi__sub">
              <span><T k="reports.list.kpi.actionRequired" /></span>
            </div>
          </div>
        </div>

        {templateOpen && (
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card__head">
              <div>
                <div className="card__title"><T k="reports.list.template.title" /></div>
                <div className="card__sub">
                  <T k="reports.list.template.placeholdersLabel" />{' '}<code>{'{userName}'}</code>, <code>{'{companyName}'}</code>,{' '}
                  <code>{'{reportCount}'}</code>, <code>{'{date}'}</code>,{' '}
                  <code>{'{generatedDate}'}</code>
                </div>
              </div>
              <button className="btn btn--ghost btn--sm" onClick={() => setTemplateOpen(false)}>
                {Icon.close({ s: 12 })}
              </button>
            </div>
            <div className="card__body">
              <div className="fld" style={{ marginBottom: 12 }}>
                <label className="fld__label"><T k="reports.list.template.subject" /></label>
                <input
                  className="fld__input"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>
              <div className="fld">
                <label className="fld__label"><T k="reports.list.template.body" /></label>
                <textarea
                  className="fld__input"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={8}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <button className="btn btn--primary" onClick={handleTemplateSave} disabled={templateLoading}>
                  {templateLoading ? t('reports.list.template.saving') : t('reports.list.template.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="tab-bar">
            {[
              ['all', t('reports.list.tab.all'), counts.all],
              ['outgoing', t('reports.list.tab.outgoing'), counts.outgoing],
              ['incoming', t('reports.list.tab.incoming'), counts.incoming],
            ].map(([k, l, c]) => (
              <button
                key={k}
                className={'tab-bar__tab ' + (dir === k ? 'is-active' : '')}
                onClick={() => setDir(k)}
              >
                {l} <span className="count">{c}</span>
              </button>
            ))}
          </div>

          <div className="filter-bar">
            <div className="search-input">
              {Icon.search({ s: 12 })}
              <input
                placeholder={t('reports.list.search.placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              <T k="reports.list.count" vars={{ count: rows.length }} />
            </span>
          </div>

          <div className="tbl-wrap">
            <table className="tbl table--cards">
              <thead>
                <tr>
                  <th><T k="reports.list.col.reportId" /></th>
                  <th><T k="reports.list.col.direction" /></th>
                  <th><T k="reports.list.col.period" /></th>
                  <th><T k="reports.list.col.partner" /></th>
                  <th className="num"><T k="reports.list.col.amount" /></th>
                  <th className="num"><T k="reports.list.col.records" /></th>
                  <th><T k="reports.list.col.status" /></th>
                  <th><T k="reports.list.col.delivered" /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-dim)' }}>
                      <T k="reports.list.loading" />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-dim)' }}>
                      <T k="reports.list.empty" />
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td className="id bold" data-label={t('reports.list.col.reportId')}>{r.id}</td>
                      <td className="muted" data-label={t('reports.list.col.direction')}>
                        {r.direction === 'incoming' ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5,
                              color: 'var(--info)',
                            }}
                          >
                            <T k="reports.list.direction.incoming" />
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5,
                              color: 'var(--ht-gold-600)',
                            }}
                          >
                            <T k="reports.list.direction.outgoing" />
                          </span>
                        )}
                      </td>
                      <td className="bold" data-label={t('reports.list.col.period')}>{r.period}</td>
                      <td data-label={t('reports.list.col.partner')}>
                        <span className="rics">{(r.partner || '').split(' · ')[0]}</span>
                        <span style={{ marginLeft: 4, color: 'var(--text-muted)', fontSize: 12 }}>
                          {(r.partner || '').split(' · ')[1] || ''}
                        </span>
                      </td>
                      <td className="num bold" data-label={t('reports.list.col.amount')}>{fmtCurEUR(r.amount)}</td>
                      <td className="num muted" data-label={t('reports.list.col.records')}>{(r.records || 0).toLocaleString()}</td>
                      <td data-label={t('reports.list.col.status')}>
                        <span
                          className={
                            'pill pill--' +
                            ({ sent: 'ok', received: 'ok', queued: 'warn', overdue: 'err' }[r.status] || 'neutral')
                          }
                        >
                          <span className="pill-dot" />
                          {r.status}
                        </span>
                      </td>
                      <td className="muted mono" data-label={t('reports.list.col.delivered')} style={{ fontSize: 11 }}>
                        {r.sent}
                      </td>
                      <td data-label={t('reports.list.col.actions')}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="btn btn--xs"
                            style={{ fontSize: 11, padding: '2px 8px', height: 26 }}
                            onClick={() => navigate(`/reports/${r.id}`)}
                            title={t('reports.list.row.viewTitle')}
                          >
                            <T k="reports.list.row.view" />
                          </button>
                          <button
                            className="iconbtn"
                            style={{ width: 26, height: 26 }}
                            onClick={() => handleDownload(r.id, 'csv')}
                            disabled={downloadingId === r.id + '-csv'}
                            title={t('reports.list.row.downloadCsvTitle')}
                          >
                            {Icon.download({ s: 13 })}
                          </button>
                          <button
                            className="iconbtn"
                            style={{ width: 26, height: 26 }}
                            onClick={() => handleDownload(r.id, 'g6')}
                            disabled={downloadingId === r.id + '-g6'}
                            title={t('reports.list.row.downloadG6Title')}
                          >
                            {Icon.external({ s: 12 })}
                          </button>
                          <button
                            className="iconbtn"
                            style={{ width: 26, height: 26 }}
                            onClick={() => navigator.clipboard?.writeText(r.id)}
                            title={t('reports.list.row.copyIdTitle')}
                          >
                            {Icon.more({ s: 13 })}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="tbl-foot">
            <span>
              <T k="reports.list.foot.a" />{' '}
              <b className="mono" style={{ color: 'var(--text)' }}>
                {fmtCurEUR(totalAmount)}
              </b>{' '}
              <T k="reports.list.foot.b" />{' '}
              <b className="mono" style={{ color: 'var(--text)' }}>
                {rows.length}
              </b>{' '}
              <T k="reports.list.foot.c" />
            </span>
            <div className="pager">
              <button className="is-active">1</button>
            </div>
          </div>
        </div>
        </div>
      </div>

      <Toast toast={toast} />

      <Modal isOpen={isModalOpen} title={t('reports.list.modal.generateTitle')} onClose={() => setIsModalOpen(false)}>
        <GenerateReport
          onSuccess={async () => {
            setIsModalOpen(false)
            await fetchReports()
          }}
          isLoading={false}
        />
      </Modal>
    </Layout>
  )
}
